import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Smartphone, Apple, Bot, ChevronLeft, Share, Plus, MoreVertical, Wifi, Zap, Bell } from 'lucide-react';

export default function InstallApp() {
    const [platform, setPlatform] = useState('ios');

    useEffect(() => {
        const ua = navigator.userAgent || '';
        if (/android/i.test(ua)) setPlatform('android');
        else if (/iPad|iPhone|iPod/.test(ua)) setPlatform('ios');
    }, []);

    return (
        <div className="min-h-screen bg-white" data-testid="install-page">
            <header className="bg-[hsl(220_13%_10%)] text-white py-5">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between">
                    <Link to="/"><Logo variant="light" /></Link>
                    <Link to="/" className="text-sm flex items-center gap-1 text-white/70 hover:text-[hsl(var(--accent))]"><ChevronLeft className="w-4 h-4" /> Home</Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
                <div className="text-center mb-10">
                    <Smartphone className="w-14 h-14 mx-auto text-[hsl(var(--accent))] mb-3" />
                    <h1 className="font-display text-5xl md:text-6xl mb-3">INSTALLA L'APP</h1>
                    <p className="text-lg text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto">
                        CantiereAI funziona come app nativa sul tuo telefono. Nessun download dallo store, nessun megabyte sprecato — solo 2 tap.
                    </p>
                </div>

                {/* Vantaggi */}
                <div className="grid sm:grid-cols-3 gap-4 mb-10">
                    {[
                        { icon: Zap, t: 'Avvio istantaneo', d: 'Icona in home, come una vera app' },
                        { icon: Wifi, t: 'Funziona offline', d: 'Le pagine viste restano disponibili' },
                        { icon: Bell, t: 'Notifiche push', d: 'Avvisi su Android (presto anche iOS 16.4+)' },
                    ].map((f, i) => (
                        <Card key={i} className="p-5 text-center">
                            <f.icon className="w-8 h-8 mx-auto text-[hsl(var(--accent))] mb-2" />
                            <div className="font-display text-lg mb-1">{f.t}</div>
                            <div className="text-xs text-[hsl(var(--muted-foreground))]">{f.d}</div>
                        </Card>
                    ))}
                </div>

                <Tabs value={platform} onValueChange={setPlatform} className="mt-6">
                    <TabsList className="w-full grid grid-cols-2 mb-4">
                        <TabsTrigger value="ios" data-testid="tab-ios"><Apple className="w-4 h-4 mr-2" /> iPhone / iPad</TabsTrigger>
                        <TabsTrigger value="android" data-testid="tab-android"><Bot className="w-4 h-4 mr-2" /> Android</TabsTrigger>
                    </TabsList>

                    <TabsContent value="ios">
                        <Card className="p-6">
                            <h2 className="font-display text-2xl mb-1">INSTALLAZIONE SU iPHONE / iPAD</h2>
                            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-5">⚠ Solo con <strong>Safari</strong> (non funziona da Chrome iOS)</p>
                            <Steps steps={[
                                { n: 1, title: 'Apri Safari', body: <>Visita <code className="bg-[hsl(var(--muted))] px-1 rounded">cantiereai.com</code> con Safari su iPhone/iPad.</> },
                                { n: 2, title: 'Tocca Condividi', body: <span className="flex items-center gap-2 flex-wrap">In basso al centro c'è l'icona <span className="inline-flex items-center justify-center w-9 h-9 rounded bg-[hsl(var(--muted))] border"><Share className="w-4 h-4" /></span> — toccala.</span> },
                                { n: 3, title: 'Aggiungi alla schermata Home', body: <span className="flex items-center gap-2 flex-wrap">Scorri verso il basso e seleziona <strong>"Aggiungi alla schermata Home"</strong> <span className="inline-flex items-center justify-center w-9 h-9 rounded bg-[hsl(var(--muted))] border"><Plus className="w-4 h-4" /></span></span> },
                                { n: 4, title: 'Conferma', body: 'Tocca "Aggiungi" in alto a destra. L\'icona CantiereAI apparirà nella tua schermata Home — toccala per aprire l\'app a tutto schermo.' },
                            ]} />
                        </Card>
                    </TabsContent>

                    <TabsContent value="android">
                        <Card className="p-6">
                            <h2 className="font-display text-2xl mb-1">INSTALLAZIONE SU ANDROID</h2>
                            <p className="text-xs text-[hsl(var(--muted-foreground))] mb-5">Funziona con Chrome, Edge, Samsung Internet, Brave</p>
                            <Steps steps={[
                                { n: 1, title: 'Apri Chrome', body: <>Visita <code className="bg-[hsl(var(--muted))] px-1 rounded">cantiereai.com</code> con Chrome sul tuo telefono.</> },
                                { n: 2, title: 'Tocca i tre puntini', body: <span className="flex items-center gap-2 flex-wrap">In alto a destra <span className="inline-flex items-center justify-center w-9 h-9 rounded bg-[hsl(var(--muted))] border"><MoreVertical className="w-4 h-4" /></span> — apri il menu.</span> },
                                { n: 3, title: 'Installa app', body: <>Seleziona <strong>"Installa app"</strong> o <strong>"Aggiungi alla schermata Home"</strong>.</> },
                                { n: 4, title: 'Pronto!', body: 'Chrome aggiungerà CantiereAI alla tua app drawer. Aprila come qualunque altra app.' },
                            ]} />
                            <div className="mt-5 p-3 rounded-lg bg-[hsl(210_90%_94%)] text-xs">
                                💡 <strong>Suggerimento:</strong> alcuni telefoni mostrano automaticamente un banner "Installa CantiereAI" — accettalo e salti tutti i passaggi.
                            </div>
                        </Card>
                    </TabsContent>
                </Tabs>

                <Card className="p-6 mt-8 bg-[hsl(220_13%_10%)] text-white">
                    <h3 className="font-display text-xl mb-2">DOMANDE FREQUENTI</h3>
                    <Faq q="Devo pagare per installare l'app?" a="No. L'installazione è gratuita. Paghi solo l'abbonamento al piano CantiereAI che scegli." />
                    <Faq q="L'app occupa molto spazio?" a="No. Meno di 5 MB. È una Progressive Web App, molto più leggera delle app native." />
                    <Faq q="Posso disinstallarla?" a="Sì, come una normale app: tieni premuta l'icona → Rimuovi." />
                    <Faq q="Le notifiche push funzionano?" a="Su Android sì (Chrome). Su iPhone solo con iOS 16.4 e successivi." />
                    <Faq q="Funziona senza internet?" a="Le pagine già visitate funzionano offline. Per dati nuovi serve connessione." />
                </Card>

                <div className="text-center mt-10">
                    <Link to="/"><Button className="bg-[hsl(var(--accent))] hover:bg-[hsl(45_100%_48%)] text-[hsl(220_13%_10%)] font-bold h-11 px-8">← Torna alla home</Button></Link>
                </div>
            </main>
        </div>
    );
}

function Steps({ steps }) {
    return (
        <div className="space-y-4">
            {steps.map((s) => (
                <div key={s.n} className="flex items-start gap-4">
                    <span className="w-9 h-9 rounded-full bg-[hsl(var(--accent))] text-[hsl(220_13%_10%)] font-display text-lg flex items-center justify-center flex-shrink-0">{s.n}</span>
                    <div>
                        <div className="font-semibold mb-0.5">{s.title}</div>
                        <div className="text-sm text-[hsl(var(--muted-foreground))]">{s.body}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}

function Faq({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-white/10 last:border-b-0 py-3">
            <button onClick={() => setOpen(!open)} className="w-full text-left flex items-center justify-between gap-2">
                <span className="font-medium text-sm">{q}</span>
                <span className="text-[hsl(var(--accent))] text-xs">{open ? '−' : '+'}</span>
            </button>
            {open && <p className="text-xs text-white/70 mt-2 pl-1">{a}</p>}
        </div>
    );
}
